/**
 * Orbital image utilities for status-based orbital ring rendering.
 * Uses a single transparent chrome image with SVG gradient for status colors.
 */

import type { ExtendedStatusValue } from './statusColors';
import { STATUS_COLORS } from './statusColors';

// Import the transparent orbital chrome image
import orbitalTransparent from '../assets/images/armillary/orbital-transparent.png';

/**
 * The transparent orbital image with chrome rings.
 * Status color is rendered as an SVG gradient behind this image.
 */
export const ORBITAL_CHROME_IMAGE = orbitalTransparent;

/**
 * Get the orbital chrome image (same for all statuses).
 * The status color is rendered separately as an SVG gradient.
 */
export function getOrbitalImage(): string {
  return ORBITAL_CHROME_IMAGE;
}

/**
 * Get the status color for the orbital ring gradient.
 */
export function getOrbitalStatusColor(status: ExtendedStatusValue | undefined): string {
  return STATUS_COLORS[status || 'unknown'] || STATUS_COLORS.unknown;
}

/**
 * Gradient stop colors for status-based orbital ring.
 * Creates a subtle gradient effect for the status color ring.
 */
export function getStatusGradientStops(status: ExtendedStatusValue | undefined): { offset: string; color: string; opacity: number }[] {
  const baseColor = STATUS_COLORS[status || 'unknown'] || STATUS_COLORS.unknown;

  return [
    { offset: '0%', color: baseColor, opacity: 0.8 },
    { offset: '50%', color: baseColor, opacity: 1 },
    { offset: '100%', color: baseColor, opacity: 0.8 },
  ];
}

/**
 * Ring dimensions for the status gradient as ratios of the node size.
 * These values position the gradient ring in the transparent gap of the chrome image.
 */
export const ORBITAL_RING_DIMENSIONS = {
  // Radius to the center of the status ring stroke (ratio of node radius)
  ringCenterRadius: 0.77,
  // Width of the status ring stroke (ratio of node size)
  strokeWidth: 0.12,
};

export default ORBITAL_CHROME_IMAGE;
